import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { useToast } from '@aslanpay/portal-common-lib';

import { SALARY_RECONCILIATIONS_PAGE_QUERY_KEY } from 'app/hooks/useFetchSalaryReconciliationsPage';

import SalaryReconciliationsApiHandler from 'app/services/salaryReconciliationsApiHandler';

import {
  QueueSalaryReconciliationRequest,
  SalaryReconciliationRun,
} from 'app/types/salaryReconciliations';

const DEFAULT_ERROR_DESCRIPTION =
  'Sorry, the salary reconciliation could not be started. Please try again.';
const QUEUE_BUSY_DESCRIPTION =
  'Another salary reconciliation is being queued. Please try again in a moment.';

/**
 * @description Explains why a run could not be queued: the queue was busy, the backend's own message, or a default.
 */
const getErrorDescription = (error: Error) => {
  if (axios.isAxiosError<{ message?: string }>(error)) {
    if (error.response?.status === 409) {
      return QUEUE_BUSY_DESCRIPTION;
    }
    return error.response?.data?.message || DEFAULT_ERROR_DESCRIPTION;
  }
  return DEFAULT_ERROR_DESCRIPTION;
};

/** Queues a salary accrual reconciliation of every company, or of the companies given. */
const useQueueSalaryReconciliation = () => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation<SalaryReconciliationRun, Error, QueueSalaryReconciliationRequest>(
    (body) => SalaryReconciliationsApiHandler.queueReconciliation(body),
    {
      onSuccess: () => {
        toast({ position: 'top-right', status: 'success', title: 'Salary reconciliation queued' });
      },
      onError: (error) => {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'Something went wrong',
          description: getErrorDescription(error),
        });
      },
      onSettled: () =>
        queryClient.invalidateQueries({ queryKey: [SALARY_RECONCILIATIONS_PAGE_QUERY_KEY] }),
    }
  );
};

export default useQueueSalaryReconciliation;
